import React from 'react';
import { array, bool, func, number, string } from 'prop-types';
import { Table } from 'antd';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { I18n as I } from 'react-redux-i18n';

import { formatCurrency, toDateTimeString, AuctionStatuses } from 'vdlib';

import Portlet from 'components/layout/Portlet';
import LookupsHelper from 'lib/LookupsHelper';
import { usePartyFlags } from 'lib/Hooks';

import BidTypeCellRenderer from 'components/tables/datagrid-columns/BidTypeCellRenderer';
import { getStaticAmountDiffCellRenderer } from 'components/tables/datagrid-columns/AmountDiffCellRenderer';
import BidSelectorWizard from './BidSelectorWizard';

const getColumnDefs = (
  { auctionStatus, auctionPartPrice, imitationCategories, usedConditionTypes },
  { isInsurance, isOperator, partyType },
  onSelecteBid,
  onTempSelectBid
) =>
  [
    {
      title: I.t('SN'),
      key: 'ID',
      dataIndex: 'ID',
      width: 25,
      align: 'center',
    },
    {
      title: I.t('AMOUNT'),
      dataIndex: 'Amount',
      align: 'center',
      width: 150,
      render: (text, record) => <div>{formatCurrency(record.Amount)}</div>,
    },
    {
      title: I.t('DIFFERENCE_FROM_INITIAL_PRICE'),
      field: 'Amount',
      align: 'center',
      width: 200,
      render: getStaticAmountDiffCellRenderer(auctionPartPrice, 'Amount', true),
    },
    {
      title: I.t('PART_TYPE'),
      field: 'PartTypeID',
      cellRendererFramework: BidTypeCellRenderer,
      width: 200,
      align: 'center',
      render: (text, record) => BidTypeCellRenderer(record),
    },
    {
      id: 'UsedConditionTypeID',
      title: I.t('USED_CONDITION_TYPE'),
      field: 'UsedConditionTypeID',
      render: (_, { UsedConditionTypeID }) =>
        LookupsHelper.getDescription(usedConditionTypes, UsedConditionTypeID),
      width: 200,
      align: 'center',
    },
    {
      id: 'ImitationCategoryID',
      title: I.t('IMITATION_CATEGORY'),
      field: 'ImitationCategoryID',
      render: (_, { ImitationCategoryID }) =>
        LookupsHelper.getDescription(imitationCategories, ImitationCategoryID),
      width: 200,
      align: 'center',
    },
    {
      id: 'Comments',
      title: I.t('COMMENTS'),
      field: 'Comments',
      width: 200,
      tooltipField: 'Comments',
    },
    {
      title: I.t('DATE'),
      width: 150,
      align: 'center',
      render: (text, record) => <div>{toDateTimeString(record.BidTime)}</div>,
    },
    isOperator && {
      title: I.t('MERCHANT'),
      dataIndex: 'Bidder',
      width: 150,
      align: 'center',
    },
    auctionStatus === AuctionStatuses.ForSelection &&
      isInsurance && {
        title: '',
        render: (_, record) => {
          return isInsurance ? (
            <div>
              <OverlayTrigger
                placement={'top'}
                overlay={
                  <Tooltip id={`lock-assign-${record.id}-tooltip`}>
                    {I.t('TEMP_SELECT')}
                  </Tooltip>
                }
              >
                <button
                  type="button"
                  className={`btn btn-xs float-right ${
                    record.IsSelected ? 'green' : 'gray'
                  }`}
                  onClick={() => onTempSelectBid(record.ID)}
                  style={{ marginLeft: 3 }}
                >
                  <i className="fa fa-thumbs-o-up" />
                </button>
              </OverlayTrigger>
              <OverlayTrigger
                placement={'top'}
                overlay={
                  <Tooltip id={`lock-assign-${record.id}-tooltip`}>
                    {I.t('ORDER')}
                  </Tooltip>
                }
              >
                <button
                  type="button"
                  className="btn btn-xs float-right blue"
                  onClick={() => onSelecteBid(record)}
                >
                  <i className="fa fa-tag" />
                </button>
              </OverlayTrigger>
            </div>
          ) : (
            false
          );
        },
      },
  ].filter(c => !!c);

const AuctionBidsTable = ({
  bids,
  onSelectBid,
  onTempSelectBid,
  partyType,
  partyTypes,
  partDescription,
  submitting,
  user,
  ...props
}) => {
  const [selectedBid, setSelectedBid] = React.useState(null);

  const columnDefs = getColumnDefs(
    props,
    usePartyFlags(user, partyTypes),
    setSelectedBid,
    onTempSelectBid
  );

  const columnsWidth = React.useMemo(
    () =>
      (columnDefs || []).reduce(
        (sum, { width } = {}) => sum + (typeof width === 'number' ? width : 0),
        400
      ),
    [columnDefs]
  );

  return (
    <Portlet
      title={`${
        I.t('OFFERS_INITIAL_PRICE_C') + formatCurrency(props.auctionPartPrice)
      }`}
    >
      <Table
        columns={columnDefs}
        dataSource={bids}
        scroll={{ x: columnsWidth }}
      />

      <BidSelectorWizard
        show={!!selectedBid}
        onSubmit={onSelectBid}
        loading={submitting}
        bid={selectedBid}
        onHide={() => setSelectedBid(null)}
        partDescription={partDescription}
      />
    </Portlet>
  );
};

AuctionBidsTable.propTypes = {
  auctionPartPrice: number,
  auctionStatus: string,
  bids: array,
  partDescription: string,
  partyType: string,
  submitting: bool,
  usedConditionTypes: array,
  imitationCategories: array,

  onSelectBid: func.isRequired,
};

export default AuctionBidsTable;
